import * as React from 'react'
import { Link } from '@tanstack/react-router'
import { Check } from 'lucide-react'
import { useI18n } from '@/app/lib/i18n'
import { contentText } from '@/app/lib/i18n/contentText'
import { ImageLoader } from '@/ui/components/ImageLoader'
import type { CertificationStatus } from '@/app/lib/api/certifications'
import * as styles from './CertificationHomePage.css'

const imgHero = '/images/certification/hero.png'

// Fallback art for modules that don't have a hero image authored yet.
export const CERTIFICATION_COURSE_FALLBACK_IMAGES: Record<string, string> = {
  'sanctuary-2': '/images/certification/sanctuary-2.png',
  'sanctuary-3': '/images/certification/sanctuary-3.png',
}

export function resolveCourseCardImageUrl(
  heroImageUrl: string | null | undefined,
  slug?: string | null,
): string {
  return heroImageUrl || (slug ? CERTIFICATION_COURSE_FALLBACK_IMAGES[slug] : undefined) || imgHero
}

export interface CertificationCourseCardProgress {
  status: CertificationStatus
  progressPercent: number
  passedCount: number
  totalCount: number
}

export interface CertificationCourseCardProps {
  title: string
  heroImageUrl?: string | null
  slug?: string | null
  /**
   * When set, renders as a TanStack `Link` to the course page.
   * When omitted, renders a non-navigating preview that keeps hover styles.
   */
  courseId?: string
  progress?: CertificationCourseCardProgress | null
  isActive?: boolean
}

export function CertificationCourseCard({
  title,
  heroImageUrl,
  slug,
  courseId,
  progress,
  isActive = true,
}: CertificationCourseCardProps) {
  const { t, tx } = useI18n()
  const imageSrc = resolveCourseCardImageUrl(heroImageUrl, slug)
  const displayTitle = contentText(t, title).trim() || '—'

  const hasProgress =
    progress != null &&
    (progress.progressPercent > 0 ||
      progress.status === 'in_progress' ||
      progress.status === 'retake_needed' ||
      progress.status === 'complete')
  const complete = progress?.status === 'complete'

  const body = (
    <>
      <div className={styles.courseImageWrap}>
        <ImageLoader src={imageSrc} alt={displayTitle} />
        {!isActive && <div className={styles.courseImageInactiveOverlay} aria-hidden />}
        {!isActive && (
          <span className={styles.courseInactiveBadge}>
            {t('certification.home.inactive')}
          </span>
        )}
        {complete && <div className={styles.courseImageCompleteOverlay} aria-hidden />}
        {hasProgress && progress && (
          <span className={styles.courseProgressBadge}>
            {t('certification.home.progressBadge', {
              passed: progress.passedCount,
              total: progress.totalCount,
              percent: progress.progressPercent,
            })}
          </span>
        )}
        {complete && (
          <span
            className={styles.courseCompleteCheck}
            aria-label={t('certification.course.completed')}
          >
            <Check size={16} strokeWidth={2.5} aria-hidden />
          </span>
        )}
      </div>
      <p className={styles.courseTitle}>{displayTitle}</p>
      <div className={styles.courseButton}>
        <span className={styles.courseButtonLabel}>
          {complete
            ? tx('certification.home.reviewCourse')
            : tx('certification.home.viewCourse')}
        </span>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <line x1="5" y1="12" x2="19" y2="12" />
          <polyline points="12 5 19 12 12 19" />
        </svg>
      </div>
    </>
  )

  if (courseId) {
    return (
      <Link
        // Spread+cast: this card is also mounted under the standalone
        // certification app's own route tree
        // (src/certification-app/routes.tsx), not the main app's
        // globally-registered router, so "/course/$courseId" isn't
        // in the `to` union TypeScript sees here. See routes.tsx.
        {...({ to: '/course/$courseId', params: { courseId } } as any)}
        className={[styles.courseCard, complete ? styles.courseCardComplete : '']
          .filter(Boolean)
          .join(' ')}
      >
        {body}
      </Link>
    )
  }

  return (
    <div className={`${styles.courseCard} ${styles.courseCardPreview}`}>
      {body}
    </div>
  )
}
